import { Typography } from '@mui/joy';
import { ArrowsClockwise, Funnel } from '@phosphor-icons/react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { AgGridTable, Button } from 'core/components';
import { SortDirection, TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider, SelectionCountProvider } from 'core/states';
import { IVendorQuoteImportAttempt } from 'dashboard/types';
import { Color, Level, Variant } from 'theme/enums';

import { columnDef } from './columns';
import { VendorQuoteLogColId } from './enums';
import { hasImportProblem } from '../utils';

interface IProps {
  attempts: IVendorQuoteImportAttempt[];
  isLoading: boolean;
  showRefresh: boolean;
  onRefresh: () => void;
}

export const VendorQuoteLogTable = ({ attempts, isLoading, showRefresh, onRefresh }: IProps) => {
  const { t } = useTranslation();

  const { user } = useAuthUser();

  // Defaults on: the reason to open this dashboard is almost always to find the
  // uploads that went wrong, and scanning a full range for them is the tedious
  // part. The toggle clears it to show every upload.
  const [showProblemsOnly, setShowProblemsOnly] = useState(true);

  const columnDefs = useMemo(() => columnDef(), []);

  const problemAttempts = useMemo(() => (attempts ?? []).filter(hasImportProblem), [attempts]);

  const visibleAttempts = showProblemsOnly ? problemAttempts : attempts;

  const onToggleProblemsOnly = useCallback(() => setShowProblemsOnly((current) => !current), []);

  // The default-on filter would otherwise leave a healthy range showing the
  // grid's stock "no rows" over cards that report a non-zero upload count — the
  // one reading of this screen that looks like a broken report. Say the range is
  // clean instead. A range with genuinely no uploads keeps the stock overlay,
  // because the element resolves to undefined and the empty state stays off.
  const emptyStateElement = useMemo(() => {
    if (!showProblemsOnly || problemAttempts.length || !attempts?.length) {
      return undefined;
    }

    return (
      <Typography level={Level.BODY_SM}>
        {t('labelNoImportProblems', { count: attempts.length })}
      </Typography>
    );
  }, [showProblemsOnly, problemAttempts.length, attempts?.length, t]);

  const initialState = useMemo(
    () => ({
      sort: {
        sortModel: [{ colId: VendorQuoteLogColId.STARTED_AT, sort: SortDirection.DESC }],
      },
    }),
    []
  );

  const actionButtons = useCallback(() => {
    const buttons = [
      <Button
        key="problemsOnlyVendorQuoteLogButton"
        text={t('labelProblemsOnly', { count: problemAttempts.length })}
        startDecorator={<Funnel weight={showProblemsOnly ? 'fill' : 'regular'} />}
        variant={showProblemsOnly ? Variant.SOLID : Variant.OUTLINED}
        color={showProblemsOnly ? Color.PRIMARY : Color.NEUTRAL}
        onClick={onToggleProblemsOnly}
        tooltipProps={{
          title: showProblemsOnly ? t('tooltipShowAllUploads') : t('tooltipShowProblemUploadsOnly'),
          children: null,
        }}
      />,
    ];

    if (!showRefresh) {
      return buttons;
    }

    return [
      ...buttons,
      <Button
        key="refreshVendorQuoteLogButton"
        icon={<ArrowsClockwise />}
        variant={Variant.OUTLINED}
        color={Color.NEUTRAL}
        loading={isLoading}
        onClick={onRefresh}
        tooltipProps={{ title: t('tooltipRefreshReport'), children: null }}
      />,
    ];
  }, [
    showRefresh,
    isLoading,
    onRefresh,
    t,
    showProblemsOnly,
    onToggleProblemsOnly,
    problemAttempts.length,
  ]);

  return (
    <SelectionCountProvider>
      <AgGridClientTableProvider
        // Saved layouts and default filters are per-user, so the grid has to be
        // rebuilt when the user changes — an admin impersonating someone else
        // would otherwise keep the previous user's saved columns and filters.
        key={user?.id}
        tableId="vendorQuoteImportLogTable"
        entityType="Upload"
        layoutType={TableLayoutType.VENDOR_QUOTE_IMPORT_LOG}
        hasLayoutActions
        hasManageFilters
        columnDefs={columnDefs}
        initialState={initialState}
        data={visibleAttempts}
        isDataLoading={isLoading}
        loadingPercentage={isLoading ? 0 : 100}
        dataCurrentPage={1}
        hasRowSelection={false}
      >
        <AgGridTable actionButtons={actionButtons} emptyStateElement={emptyStateElement} />
      </AgGridClientTableProvider>
    </SelectionCountProvider>
  );
};
